import { useState } from 'react';
import { Button } from '@/components/ui/Button';

interface Step3Props {
  onNext: (data: Step3Data) => void;
  onBack: () => void;
  initialData?: Step3Data;
}

export interface Step3Data {
  features: string[];
  customFeatures?: string;
}

const commonFeatures = [
  { value: 'auth', label: 'User Authentication', description: 'Login, registration, password reset' },
  { value: 'payments', label: 'Payment Processing', description: 'Accept online payments' },
  { value: 'admin', label: 'Admin Panel', description: 'Manage users, content, settings' },
  { value: 'api', label: 'API Integration', description: 'Connect to external services' },
  { value: 'realtime', label: 'Real-time Updates', description: 'Live data, notifications' },
  { value: 'upload', label: 'File Upload', description: 'Upload and manage files' },
  { value: 'search', label: 'Search Functionality', description: 'Search through data' },
  { value: 'analytics', label: 'Analytics', description: 'Track usage and metrics' },
  { value: 'email', label: 'Email/Notifications', description: 'Automated emails and alerts' },
  { value: 'multilang', label: 'Multi-language', description: 'Support multiple languages' },
];

export const Step3Features = ({ onNext, onBack, initialData }: Step3Props) => {
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>(initialData?.features || []);
  const [customFeatures, setCustomFeatures] = useState(initialData?.customFeatures || '');

  const toggleFeature = (feature: string) => {
    setSelectedFeatures((prev) =>
      prev.includes(feature) ? prev.filter((f) => f !== feature) : [...prev, feature]
    );
  };

  const handleSubmit = () => {
    onNext({ features: selectedFeatures, customFeatures });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">What features do you need?</h2>
        <p className="text-gray-600 mt-2">Select all the features your project requires</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {commonFeatures.map((feature) => (
          <div
            key={feature.value}
            onClick={() => toggleFeature(feature.value)}
            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
              selectedFeatures.includes(feature.value)
                ? 'border-blue-600 bg-blue-50'
                : 'border-gray-200 hover:border-blue-300'
            }`}
          >
            <div className="flex items-start">
              <div className="flex-shrink-0 mt-1">
                <div
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                    selectedFeatures.includes(feature.value)
                      ? 'bg-blue-600 border-blue-600'
                      : 'border-gray-300'
                  }`}
                >
                  {selectedFeatures.includes(feature.value) && (
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 12 12">
                      <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="2" fill="none" />
                    </svg>
                  )}
                </div>
              </div>
              <div className="ml-3">
                <h4 className="font-semibold text-gray-900">{feature.label}</h4>
                <p className="text-sm text-gray-600">{feature.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div>
        <label htmlFor="custom" className="block text-sm font-medium text-gray-700 mb-2">
          Additional Features (Optional)
        </label>
        <textarea
          id="custom"
          value={customFeatures}
          onChange={(e) => setCustomFeatures(e.target.value)}
          rows={3}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Describe any custom features not listed above..."
        />
      </div>

      <div className="flex justify-between">
        <Button onClick={onBack} variant="outline" className="px-8">
          Back
        </Button>
        <Button
          onClick={handleSubmit}
          className="bg-blue-600 hover:bg-blue-700 text-white px-8"
        >
          Next
        </Button>
      </div>
    </div>
  );
};
